import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCamera,
    faTimes,
    faEdit,
    faSave,
    faExternalLinkAlt,
    faEnvelope,
    faUserShield,
    faStore
} from '@fortawesome/free-solid-svg-icons';
import { faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { profilePictureUrl } from '../utils/storageUrls';

const LIMITS = {
    displayName: 99,
    bio: 1000,
    location: 50,
    twitterHandle: 50,
    youtubeChannel: 100,
    bricklinkStore: 100
};

const FieldError = ({ error }) => {
    if (!error) return null;
    return <div className="mt-1 text-sm text-red-600">{error}</div>;
};

const CharacterCount = ({ current = 0, max }) => (
    <div className="mt-1 text-xs text-gray-500 text-right">{current}/{max}</div>
);

const sectionCardClass = 'bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6';
const inputClass = (hasError) =>
    `w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${
        hasError ? 'border-red-500' : 'border-gray-300'
    }`;

const Profile = () => {
    const { user } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [profileData, setProfileData] = useState({
        displayName: '',
        bio: '',
        location: '',
        favoriteTheme: '',
        profilePicture: null,
        twitterHandle: '',
        youtubeChannel: '',
        bricklinkStore: '',
        showEmail: false,
        isCollectionPrivate: false
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [themes, setThemes] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isProfilePhotoLoading, setIsProfilePhotoLoading] = useState(false);
    const [didProfilePhotoFail, setDidProfilePhotoFail] = useState(false);
    const [isProfileDataLoading, setIsProfileDataLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useDocumentTitle('Profile');

    useEffect(() => {
        if (user) {
            fetchProfileData();
            fetchThemes();
        }
    }, [user]);

    const displayNameForHeader = useMemo(
        () => profileData.displayName || user?.username || 'My Profile',
        [profileData.displayName, user?.username]
    );
    const remoteProfilePhotoSrc = profilePictureUrl(profileData.profilePicture) || '';
    const shouldLoadRemotePhoto = !previewImage && !!remoteProfilePhotoSrc;
    const profileImageSrc = previewImage || (shouldLoadRemotePhoto && !didProfilePhotoFail ? remoteProfilePhotoSrc : '/images/lego_user.png');
    const showPhotoSkeleton = isProfileDataLoading || (shouldLoadRemotePhoto && isProfilePhotoLoading);

    useEffect(() => {
        if (isProfileDataLoading) return;

        if (shouldLoadRemotePhoto) {
            setIsProfilePhotoLoading(true);
            setDidProfilePhotoFail(false);
            return;
        }

        setIsProfilePhotoLoading(false);
        setDidProfilePhotoFail(false);
    }, [isProfileDataLoading, shouldLoadRemotePhoto, remoteProfilePhotoSrc]);

    const validateField = (name, value) => {
        const errors = {};
        switch (name) {
            case 'displayName':
                if (value && value.length > LIMITS.displayName) {
                    errors[name] = `Display name must be ${LIMITS.displayName} characters or less`;
                }
                if (/[\u{1F300}-\u{1F9FF}]/u.test(value)) {
                    errors[name] = 'Display name cannot contain emojis';
                }
                break;
            case 'bio':
                if (value && value.length > LIMITS.bio) {
                    errors[name] = `Bio must be ${LIMITS.bio} characters or less`;
                }
                break;
            case 'location':
                if (value && value.length > LIMITS.location) {
                    errors[name] = `Location must be ${LIMITS.location} characters or less`;
                }
                if (/[\u{1F300}-\u{1F9FF}]/u.test(value)) {
                    errors[name] = 'Location cannot contain emojis';
                }
                break;
            case 'twitterHandle':
                if (value && value.length > LIMITS.twitterHandle) {
                    errors[name] = `X handle must be ${LIMITS.twitterHandle} characters or less`;
                }
                if (/[\u{1F300}-\u{1F9FF}]/u.test(value)) {
                    errors[name] = 'X handle cannot contain emojis';
                }
                break;
            case 'youtubeChannel':
                if (value && value.length > LIMITS.youtubeChannel) {
                    errors[name] = `YouTube channel must be ${LIMITS.youtubeChannel} characters or less`;
                }
                if (/[\u{1F300}-\u{1F9FF}]/u.test(value)) {
                    errors[name] = 'YouTube channel cannot contain emojis';
                }
                break;
            case 'bricklinkStore':
                if (value && value.length > LIMITS.bricklinkStore) {
                    errors[name] = `Bricklink store must be ${LIMITS.bricklinkStore} characters or less`;
                }
                if (/[\u{1F300}-\u{1F9FF}]/u.test(value)) {
                    errors[name] = 'Bricklink store cannot contain emojis';
                }
                if (value && !/^[a-zA-Z0-9-_]+$/.test(value)) {
                    errors[name] = 'Bricklink store can only contain letters, numbers, hyphens, and underscores';
                }
                break;
            default:
                break;
        }
        return errors;
    };

    const handleInputChange = (name, value) => {
        const fieldErrors = validateField(name, value);
        setProfileData((prev) => ({ ...prev, [name]: value }));
        setValidationErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return { ...next, ...fieldErrors };
        });
    };

    const validateAllFields = () => {
        const errors = {};
        Object.keys(profileData).forEach((key) => {
            if (LIMITS[key]) {
                const fieldErrors = validateField(key, profileData[key]);
                Object.assign(errors, fieldErrors);
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const normalizeLineBreaks = (text) => {
        if (!text) return text;
        const standardized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const normalized = standardized.replace(/\n{3,}/g, '\n\n');
        return normalized.trim();
    };

    const fetchProfileData = async () => {
        setIsProfileDataLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get_profile.php?user_id=${user.user_id}`);
            if (response.data.success) {
                setProfileData({
                    ...response.data.profile,
                    twitterHandle: response.data.profile.twitterHandle || '',
                    youtubeChannel: response.data.profile.youtubeChannel || '',
                    bricklinkStore: response.data.profile.bricklinkStore || '',
                    showEmail: response.data.profile.showEmail || false,
                    isCollectionPrivate: response.data.profile.isCollectionPrivate || false
                });
            }
        } catch (fetchError) {
            console.error('Error fetching profile:', fetchError);
            setError('Failed to load profile data');
        } finally {
            setIsProfileDataLoading(false);
        }
    };

    const fetchThemes = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get_parent_themes.php`);
            setThemes(response.data);
        } catch (fetchError) {
            console.error('Error fetching themes:', fetchError);
            setError('Failed to load themes');
        }
    };

    const handleImageChange = (e) => {
        setError('');
        setSuccess('');

        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);

        const formData = new FormData();
        formData.append('profile_picture', file);
        formData.append('user_id', user.user_id);

        setIsUploadingPhoto(true);
        uploadProfilePicture(formData);
    };

    const uploadProfilePicture = async (formData) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/upload_profile_picture.php`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.success) {
                setSuccess('Profile picture updated successfully!');
                setProfileData((prev) => ({
                    ...prev,
                    profilePicture: response.data.filename
                }));
            } else {
                setError('Failed to upload profile picture');
            }
        } catch (uploadError) {
            setError('Error uploading profile picture');
            console.error('Error:', uploadError);
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handleProfileUpdate = async () => {
        setError('');
        setSuccess('');

        if (!validateAllFields()) {
            setError('Please correct the validation errors before saving');
            return;
        }

        try {
            const requestData = {
                ...profileData,
                bio: normalizeLineBreaks(profileData.bio),
                showEmail: Number(profileData.showEmail),
                isCollectionPrivate: Number(profileData.isCollectionPrivate),
                user_id: user.user_id
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/update_profile.php`,
                requestData
            );

            if (response.data.success) {
                setSuccess('Profile updated successfully!');
                setIsEditing(false);
                setValidationErrors({});
            } else {
                setError(response.data.error || 'Failed to update profile');
            }
        } catch (updateError) {
            console.error('Profile update error:', updateError);
            setError('Error updating profile');
        }
    };

    if (!user) {
        return (
            <div className="flex justify-center items-center p-10 text-lg text-gray-600">
                Please log in to view your profile.
            </div>
        );
    }

    const toggleEditMode = () => {
        setError('');
        setSuccess('');
        setIsEditing((prev) => !prev);
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <div className="rounded-2xl shadow-lg border border-gray-200 bg-white overflow-hidden mb-6">
                <div className="h-14 sm:h-16 bg-gradient-to-r from-red-600 via-red-500 to-red-600" />
                <div className="px-6 sm:px-8 pt-6 pb-8">
                    <div className="flex flex-col md:flex-row md:items-center gap-5">
                        <div className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 shadow-xl bg-white shrink-0 overflow-visible transition-all ${
                            isUploadingPhoto
                                ? 'border-amber-200 ring-4 ring-amber-400/70'
                                : 'border-white ring-4 ring-gray-100'
                        }`}>
                            {isUploadingPhoto && (
                                <span className="pointer-events-none absolute -inset-[6px] rounded-full border-[3px] border-amber-400 border-t-transparent animate-spin" />
                            )}
                            <div className="w-full h-full rounded-full overflow-hidden">
                                {showPhotoSkeleton && (
                                    <div className="absolute inset-0 rounded-full animate-pulse bg-gray-200" aria-hidden="true" />
                                )}
                                {!isProfileDataLoading && (
                                    <img
                                        src={profileImageSrc}
                                        alt={displayNameForHeader}
                                        className={`w-full h-full object-cover transition-opacity duration-200 ${
                                            showPhotoSkeleton ? 'opacity-0' : 'opacity-100'
                                        }`}
                                        onLoad={() => {
                                            if (shouldLoadRemotePhoto) {
                                                setIsProfilePhotoLoading(false);
                                            }
                                        }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            setIsProfilePhotoLoading(false);
                                            if (shouldLoadRemotePhoto) {
                                                setDidProfilePhotoFail(true);
                                            } else {
                                                e.target.src = '/images/lego_user.png';
                                            }
                                        }}
                                    />
                                )}
                            </div>
                            {isEditing && (
                                <label className={`absolute -bottom-1 -right-1 w-10 h-10 bg-red-600 rounded-full flex items-center justify-center shadow-md transition-colors border-2 border-white z-10 ${isUploadingPhoto ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700 cursor-pointer'}`}>
                                    <FontAwesomeIcon icon={faCamera} className="text-white text-sm" />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        disabled={isUploadingPhoto}
                                        className="hidden"
                                        aria-label="Upload profile picture"
                                    />
                                </label>
                            )}
                        </div>

                        <div className="flex-1">
                            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">{displayNameForHeader}</h1>
                            <p className="text-gray-600 mt-1">@{user.username}</p>
                        </div>

                        <div className="w-full md:w-auto">
                            <button
                                className={`w-full px-5 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 ${
                                    isEditing
                                        ? 'bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg shadow-sm'
                                        : 'bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-md'
                                }`}
                                onClick={toggleEditMode}
                            >
                                <FontAwesomeIcon icon={isEditing ? faTimes : faEdit} />
                                <span>{isEditing ? 'Cancel Edit' : 'Edit Profile'}</span>
                            </button>
                            {isEditing && (
                                <button
                                    className="w-full mt-2 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2 disabled:bg-red-300 disabled:cursor-not-allowed"
                                    onClick={handleProfileUpdate}
                                    disabled={Object.keys(validationErrors).length > 0}
                                >
                                    <FontAwesomeIcon icon={faSave} />
                                    <span>Save Changes</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-center">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-center">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <section className={sectionCardClass}>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Profile Details</h2>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                                {isEditing ? (
                                    <>
                                        <input
                                            type="text"
                                            value={profileData.displayName || ''}
                                            onChange={(e) => handleInputChange('displayName', e.target.value)}
                                            maxLength={LIMITS.displayName}
                                            className={inputClass(!!validationErrors.displayName)}
                                        />
                                        <FieldError error={validationErrors.displayName} />
                                        <CharacterCount current={profileData.displayName?.length || 0} max={LIMITS.displayName} />
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-800">
                                        {profileData.displayName || user.username}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                                {isEditing ? (
                                    <>
                                        <textarea
                                            value={profileData.bio || ''}
                                            onChange={(e) => handleInputChange('bio', e.target.value)}
                                            maxLength={LIMITS.bio}
                                            placeholder="Tell the community a bit about your LEGO journey..."
                                            className={`${inputClass(!!validationErrors.bio)} min-h-[130px] resize-y`}
                                        />
                                        <FieldError error={validationErrors.bio} />
                                        <CharacterCount current={profileData.bio?.length || 0} max={LIMITS.bio} />
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 whitespace-pre-wrap min-h-[80px]">
                                        {profileData.bio || 'No bio yet'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className={sectionCardClass}>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Links & Community</h2>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">X Handle</label>
                                {isEditing ? (
                                    <>
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-600">
                                                @
                                            </span>
                                            <input
                                                type="text"
                                                value={profileData.twitterHandle || ''}
                                                onChange={(e) => handleInputChange('twitterHandle', e.target.value)}
                                                maxLength={LIMITS.twitterHandle}
                                                placeholder="username"
                                                className={`${inputClass(!!validationErrors.twitterHandle)} rounded-l-none`}
                                            />
                                        </div>
                                        <FieldError error={validationErrors.twitterHandle} />
                                        <CharacterCount current={profileData.twitterHandle?.length || 0} max={LIMITS.twitterHandle} />
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                                        {profileData.twitterHandle ? (
                                            <a
                                                href={`https://x.com/${profileData.twitterHandle}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                @{profileData.twitterHandle}
                                            </a>
                                        ) : (
                                            'Not linked'
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">YouTube Channel</label>
                                {isEditing ? (
                                    <>
                                        <input
                                            type="text"
                                            value={profileData.youtubeChannel || ''}
                                            onChange={(e) => handleInputChange('youtubeChannel', e.target.value)}
                                            maxLength={LIMITS.youtubeChannel}
                                            placeholder="Channel URL or ID"
                                            className={inputClass(!!validationErrors.youtubeChannel)}
                                        />
                                        <FieldError error={validationErrors.youtubeChannel} />
                                        <CharacterCount current={profileData.youtubeChannel?.length || 0} max={LIMITS.youtubeChannel} />
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                                        {profileData.youtubeChannel ? (
                                            <a
                                                href={`https://youtube.com/${profileData.youtubeChannel}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {profileData.youtubeChannel}
                                            </a>
                                        ) : (
                                            'Not linked'
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">BrickLink Store</label>
                                {isEditing ? (
                                    <>
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 text-sm">
                                                store.bricklink.com/
                                            </span>
                                            <input
                                                type="text"
                                                value={profileData.bricklinkStore || ''}
                                                onChange={(e) => handleInputChange('bricklinkStore', e.target.value)}
                                                maxLength={LIMITS.bricklinkStore}
                                                placeholder="storename"
                                                className={`${inputClass(!!validationErrors.bricklinkStore)} rounded-l-none`}
                                            />
                                        </div>
                                        <FieldError error={validationErrors.bricklinkStore} />
                                        <CharacterCount current={profileData.bricklinkStore?.length || 0} max={LIMITS.bricklinkStore} />
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                                        {profileData.bricklinkStore ? (
                                            <a
                                                href={`https://store.bricklink.com/${profileData.bricklinkStore}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {profileData.bricklinkStore}
                                            </a>
                                        ) : (
                                            'Not linked'
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </div>

                <div className="space-y-5">
                    <section className={sectionCardClass}>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Public Profile Settings</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                {isEditing ? (
                                    <>
                                        <input
                                            type="text"
                                            value={profileData.location || ''}
                                            onChange={(e) => handleInputChange('location', e.target.value)}
                                            maxLength={LIMITS.location}
                                            className={inputClass(!!validationErrors.location)}
                                        />
                                        <FieldError error={validationErrors.location} />
                                        <CharacterCount current={profileData.location?.length || 0} max={LIMITS.location} />
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                                        {profileData.location || 'Not specified'}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Favorite Theme</label>
                                {isEditing ? (
                                    <select
                                        value={profileData.favoriteTheme || ''}
                                        onChange={(e) => handleInputChange('favoriteTheme', e.target.value)}
                                        className={inputClass(false)}
                                    >
                                        <option value="">Select a theme</option>
                                        {themes.map((theme) => (
                                            <option key={theme.id} value={theme.id}>
                                                {theme.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                                        {themes.find((t) => t.id === profileData.favoriteTheme)?.name || 'Not specified'}
                                    </div>
                                )}
                            </div>

                            <div className="pt-2">
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={!!profileData.showEmail}
                                        onChange={(e) => handleInputChange('showEmail', e.target.checked)}
                                        disabled={!isEditing}
                                        className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-800">Show email publicly</span>
                                        <span className="block text-xs text-gray-500">Allows visitors to see your email on profile cards.</span>
                                    </span>
                                </label>
                            </div>

                            <div>
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={!!profileData.isCollectionPrivate}
                                        onChange={(e) => handleInputChange('isCollectionPrivate', e.target.checked)}
                                        disabled={!isEditing}
                                        className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-800">Make collection private</span>
                                        <span className="block text-xs text-gray-500">When enabled, other users cannot view your collection or wishlist.</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </section>

                    <section className={sectionCardClass}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Public Links & Privacy</h3>
                        <div className="space-y-2.5 text-sm">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
                                Email visibility: <span className="font-medium text-gray-900">{profileData.showEmail ? 'Public' : 'Private'}</span>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
                                Collection visibility: <span className="font-medium text-gray-900">{profileData.isCollectionPrivate ? 'Private' : 'Public'}</span>
                            </div>
                        </div>

                        <div className="mt-4 space-y-2">
                            {profileData.twitterHandle && (
                                <a
                                    href={`https://x.com/${profileData.twitterHandle}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faXTwitter} className="text-gray-800" />
                                        <span>@{profileData.twitterHandle}</span>
                                    </span>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-gray-400 text-xs" />
                                </a>
                            )}
                            {profileData.youtubeChannel && (
                                <a
                                    href={`https://youtube.com/${profileData.youtubeChannel}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faYoutube} className="text-red-600" />
                                        <span>{profileData.youtubeChannel}</span>
                                    </span>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-gray-400 text-xs" />
                                </a>
                            )}
                            {profileData.bricklinkStore && (
                                <a
                                    href={`https://store.bricklink.com/${profileData.bricklinkStore}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faStore} className="text-red-600" />
                                        <span>{profileData.bricklinkStore}</span>
                                    </span>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-gray-400 text-xs" />
                                </a>
                            )}
                            {!profileData.twitterHandle && !profileData.youtubeChannel && !profileData.bricklinkStore && (
                                <div className="rounded-lg border border-dashed border-gray-300 px-3 py-3 text-gray-500 text-sm">
                                    No public links added yet.
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>

        </div>
    );
};

export default Profile;
